import { CalendarClock, Loader2, Play, Trash2 } from "lucide-react"
import * as React from "react"
import { Link } from "react-router-dom"
import { toast } from "sonner"

import { EmptyState } from "@/components/empty-state"
import { PageHeader } from "@/components/layout"
import { RUN_STATUS_LABEL_KEYS } from "@/components/status-badge"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { useLocale, useT } from "@/i18n/provider"
import { ApiError, api, toastApiError } from "@/lib/api"
import type { Agent, RunStatus, Schedule } from "@/lib/types"
import { formatDateTime, formatInZone, formatRelative } from "@/lib/utils"

/** Localizes a raw run-status string via the shared run-status map; unknown values pass through. */
function translateRunStatus(t: (key: string) => string, status: string): string {
  const labelKey = RUN_STATUS_LABEL_KEYS[status as RunStatus]
  return labelKey ? t(`common.status.run.${labelKey}`) : status
}

/** Preset cron shortcuts. `labelKey` names a `schedules.presets.*` catalog entry. */
const PRESETS = [
  { labelKey: "schedules.presets.every15", value: "*/15 * * * *" },
  { labelKey: "schedules.presets.hourly", value: "0 * * * *" },
  { labelKey: "schedules.presets.weekdayNine", value: "0 9 * * 1-5" },
  { labelKey: "schedules.presets.dailyMidnight", value: "0 0 * * *" },
  { labelKey: "schedules.presets.mondaysEight", value: "0 8 * * 1" },
]

type Kind = Schedule["kind"]

/** Tomorrow 09:00 in the browser's zone, in the format a datetime-local input takes. */
function tomorrowAtNine(): string {
  const date = new Date()
  date.setDate(date.getDate() + 1)
  date.setHours(9, 0, 0, 0)
  const pad = (value: number) => String(value).padStart(2, "0")
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

export function SchedulesPage() {
  const t = useT()
  const [schedules, setSchedules] = React.useState<Schedule[]>([])
  const [agents, setAgents] = React.useState<Agent[]>([])
  const [loading, setLoading] = React.useState(true)
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const load = React.useCallback(async () => {
    try {
      const [scheduleList, agentList] = await Promise.all([
        api.listSchedules(),
        api.listAgents(),
      ])
      setSchedules(scheduleList)
      setAgents(agentList)
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : t("schedules.toasts.loadFailed"))
    } finally {
      setLoading(false)
    }
  }, [t])

  React.useEffect(() => {
    void load()
  }, [load])

  return (
    <>
      <PageHeader
        title={t("schedules.title")}
        description={t("schedules.description")}
        action={
          <Button onClick={() => setDialogOpen(true)} disabled={agents.length === 0}>
            {t("schedules.newSchedule")}
          </Button>
        }
      />

      <div className="space-y-4 p-6">
        {loading ? null : schedules.length === 0 ? (
          <EmptyState
            icon={CalendarClock}
            title={t("schedules.empty.title")}
            description={
              agents.length === 0
                ? t("schedules.empty.noAgents")
                : t("schedules.empty.description")
            }
            actionLabel={agents.length > 0 ? t("schedules.empty.action") : undefined}
            onAction={agents.length > 0 ? () => setDialogOpen(true) : undefined}
          />
        ) : (
          schedules.map((schedule) => (
            <ScheduleCard key={schedule.id} schedule={schedule} onChange={load} />
          ))
        )}

        {schedules.length > 0 && (
          <Alert>
            <AlertTitle>{t("schedules.missedRunsTitle")}</AlertTitle>
            <AlertDescription>{t("schedules.missedRunsDescription")}</AlertDescription>
          </Alert>
        )}
      </div>

      <ScheduleDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        agents={agents}
        onCreated={load}
      />
    </>
  )
}

function StatusBadge({ schedule }: { schedule: Schedule }) {
  const t = useT()
  if (schedule.kind === "once" && !schedule.is_active) {
    switch (schedule.disabled_reason) {
      case "completed":
        return <Badge variant="secondary">{t("schedules.status.completed")}</Badge>
      case "fired":
        return <Badge variant="secondary">{t("schedules.status.fired")}</Badge>
      case "too_many_failures":
        return <Badge variant="destructive">{t("schedules.status.failed")}</Badge>
    }
  }
  if (schedule.is_active) {
    return (
      <Badge variant="success">
        {schedule.kind === "once" ? t("schedules.status.pending") : t("schedules.status.active")}
      </Badge>
    )
  }
  return (
    <Badge variant="secondary">
      {schedule.disabled_reason
        ? t("schedules.status.pausedReason", { reason: schedule.disabled_reason })
        : t("schedules.status.paused")}
    </Badge>
  )
}

function ScheduleCard({
  schedule,
  onChange,
}: {
  schedule: Schedule
  onChange: () => Promise<void>
}) {
  const t = useT()
  const { locale } = useLocale()
  const [busy, setBusy] = React.useState<string | null>(null)
  // Re-enabling a job that already ran would fire it again immediately.
  const canToggle =
    schedule.kind === "cron" || !["completed", "fired"].includes(schedule.disabled_reason ?? "")

  async function withBusy(label: string, work: () => Promise<void>) {
    setBusy(label)
    try {
      await work()
    } catch (error) {
      toastApiError(error, t("common.actionFailed"))
    } finally {
      setBusy(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>{schedule.name}</CardTitle>
          <StatusBadge schedule={schedule} />
          {schedule.kind === "cron" ? (
            <Badge variant="outline" className="payload">
              {schedule.cron_expr}
            </Badge>
          ) : (
            <Badge variant="outline">
              {t("schedules.onceBadge", {
                when: formatInZone(schedule.run_at, schedule.timezone, locale),
              })}
            </Badge>
          )}
          <Badge variant="outline">{schedule.timezone}</Badge>
        </div>
        <CardDescription>
          {schedule.agent_name}
          {schedule.next_run_at &&
            t("schedules.nextRunSuffix", { when: formatDateTime(schedule.next_run_at, locale) })}
          {schedule.conversation_id && (
            <>
              {" · "}
              <Link
                to={`/chat/${schedule.conversation_id}`}
                className="underline underline-offset-4"
              >
                {t("schedules.setInChat")}
              </Link>
            </>
          )}
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-muted-foreground line-clamp-2 text-sm">{schedule.prompt}</p>

        <div className="text-muted-foreground flex flex-wrap gap-x-6 gap-y-1 text-xs">
          <span>{t("schedules.lastRun", { when: formatRelative(schedule.last_run_at, locale) })}</span>
          {schedule.last_run_status && (
            <span>
              {t("schedules.outcome", { status: translateRunStatus(t, schedule.last_run_status) })}
            </span>
          )}
          {schedule.consecutive_failures > 0 && (
            <span className="text-destructive">
              {t("schedules.consecutiveFailures", { count: schedule.consecutive_failures })}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {canToggle && (
            <div className="flex items-center gap-2">
              <Switch
                id={`active-${schedule.id}`}
                checked={schedule.is_active}
                disabled={busy !== null}
                onCheckedChange={(checked) =>
                  withBusy("toggle", async () => {
                    await api.updateSchedule(schedule.id, { is_active: checked })
                    await onChange()
                  })
                }
              />
              <Label htmlFor={`active-${schedule.id}`} className="text-xs font-normal">
                {t("common.enabled")}
              </Label>
            </div>
          )}

          <Button
            size="sm"
            variant="outline"
            disabled={busy !== null}
            onClick={() =>
              withBusy("run", async () => {
                const run = await api.runScheduleNow(schedule.id)
                toast.success(
                  t("schedules.toasts.runFinished", { status: translateRunStatus(t, run.status) }),
                )
                await onChange()
              })
            }
          >
            {busy === "run" ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Play className="size-4" />
            )}
            {t("schedules.runNow")}
          </Button>

          <Button
            size="sm"
            variant="ghost"
            className="text-destructive ml-auto"
            disabled={busy !== null}
            onClick={() =>
              withBusy("delete", async () => {
                if (!confirm(t("schedules.deleteConfirm", { name: schedule.name }))) return
                await api.deleteSchedule(schedule.id)
                toast.success(t("schedules.toasts.deleted"))
                await onChange()
              })
            }
          >
            <Trash2 className="size-4" />
            {t("schedules.delete")}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function ScheduleDialog({
  open,
  onOpenChange,
  agents,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  agents: Agent[]
  onCreated: () => Promise<void>
}) {
  const t = useT()
  const { locale } = useLocale()
  const [agentId, setAgentId] = React.useState("")
  const [name, setName] = React.useState("")
  const [kind, setKind] = React.useState<Kind>("cron")
  const [cronExpr, setCronExpr] = React.useState("0 9 * * 1-5")
  const [runAt, setRunAt] = React.useState(tomorrowAtNine)
  const [timezone, setTimezone] = React.useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  )
  const [prompt, setPrompt] = React.useState("")
  const [preview, setPreview] = React.useState<string[]>([])
  const [previewError, setPreviewError] = React.useState<string | null>(null)
  const [busy, setBusy] = React.useState(false)

  React.useEffect(() => {
    if (open && agents.length > 0) setAgentId((current) => current || agents[0].id)
  }, [open, agents])

  // The preview doubles as validation: most cron mistakes are valid expressions that
  // mean something other than intended.
  React.useEffect(() => {
    if (!open || kind !== "cron" || !cronExpr.trim()) return
    const timer = setTimeout(() => {
      api
        .previewCron(cronExpr, timezone)
        .then((result) => {
          setPreview(result.next_runs)
          setPreviewError(null)
        })
        .catch((error) => {
          setPreview([])
          setPreviewError(error instanceof ApiError ? error.message : t("schedules.dialog.invalidExpression"))
        })
    }, 350)
    return () => clearTimeout(timer)
  }, [cronExpr, timezone, open, kind, t])

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    setBusy(true)
    try {
      // run_at goes without an offset: the API reads it as wall-clock time in `timezone`.
      const timing = kind === "cron" ? { kind, cron_expr: cronExpr } : { kind, run_at: runAt }
      await api.createSchedule({ agent_id: agentId, name, timezone, prompt, ...timing })
      toast.success(t("schedules.toasts.created"))
      onOpenChange(false)
      setName("")
      setPrompt("")
      setRunAt(tomorrowAtNine())
      await onCreated()
    } catch (error) {
      toastApiError(error, t("schedules.toasts.createFailed"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("schedules.newSchedule")}</DialogTitle>
          <DialogDescription>{t("schedules.dialog.description")}</DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sched-agent">{t("schedules.dialog.agentLabel")}</Label>
            <Select value={agentId} onValueChange={setAgentId}>
              <SelectTrigger id="sched-agent">
                <SelectValue placeholder={t("schedules.dialog.agentPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sched-name">{t("common.name")}</Label>
            <Input
              id="sched-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder={
                kind === "cron"
                  ? t("schedules.dialog.namePlaceholderCron")
                  : t("schedules.dialog.namePlaceholderOnce")
              }
            />
          </div>

          <div className="space-y-2">
            <Label>{t("schedules.dialog.whenLabel")}</Label>
            <Tabs value={kind} onValueChange={(value) => setKind(value as Kind)}>
              <TabsList className="w-full">
                <TabsTrigger value="cron">{t("schedules.dialog.recurring")}</TabsTrigger>
                <TabsTrigger value="once">{t("schedules.dialog.once")}</TabsTrigger>
              </TabsList>
            </Tabs>

            {kind === "cron" ? (
              <>
                <div className="flex flex-wrap gap-1">
                  {PRESETS.map((preset) => (
                    <Button
                      key={preset.value}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setCronExpr(preset.value)}
                    >
                      {t(preset.labelKey)}
                    </Button>
                  ))}
                </div>
                <Input
                  id="sched-cron"
                  aria-label={t("schedules.dialog.cronAria")}
                  required
                  className="payload"
                  value={cronExpr}
                  onChange={(event) => setCronExpr(event.target.value)}
                  placeholder="0 9 * * 1-5"
                />
              </>
            ) : (
              <Input
                id="sched-run-at"
                aria-label={t("schedules.dialog.dateTimeAria")}
                type="datetime-local"
                required
                value={runAt}
                onChange={(event) => setRunAt(event.target.value)}
              />
            )}

            <Input
              aria-label={t("schedules.dialog.timezoneAria")}
              value={timezone}
              onChange={(event) => setTimezone(event.target.value)}
              placeholder="UTC"
            />

            {kind === "cron" &&
              (previewError ? (
                <p className="text-destructive text-xs">{previewError}</p>
              ) : preview.length > 0 ? (
                <div className="text-muted-foreground text-xs">
                  <div className="mb-1 font-medium">{t("schedules.dialog.nextRunsLabel")}</div>
                  <ul className="space-y-0.5">
                    {preview.slice(0, 3).map((moment) => (
                      <li key={moment}>{formatDateTime(moment, locale)}</li>
                    ))}
                  </ul>
                </div>
              ) : null)}
          </div>

          <div className="space-y-2">
            <Label htmlFor="sched-prompt">{t("schedules.dialog.promptLabel")}</Label>
            <Textarea
              id="sched-prompt"
              required
              rows={4}
              value={prompt}
              onChange={(event) => setPrompt(event.target.value)}
              placeholder={
                kind === "cron"
                  ? t("schedules.dialog.promptPlaceholderCron")
                  : t("schedules.dialog.promptPlaceholderOnce")
              }
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={busy || (kind === "cron" && previewError !== null)}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              {t("schedules.dialog.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
